import React, { useEffect, useState, useSyncExternalStore } from "react";
import { Popover } from "antd";
import messages from "../errors/messages.en-IN.json";
import {
  subscribeHardwareStatus,
  getHardwareStatus,
  startHardwareWatch,
  checkHardwareNow,
  isWatchdogEnabled,
  getControllerDownloadUrl,
  printQueuedNow,
  removeQueuedPrint,
  retryAllQueued,
} from "../lib/hardware/hardwareStatus";

/**
 * Web-mode Hardware Controller chip for the POS header: shows whether CW Hardware Controller
 * is reachable, what to do when it is not, and the bills waiting to print. Hidden in CW Browser
 * (Electron), when the till has no Controller address, or when posConfig hcWatchdog is "N".
 */

const hw = () => messages?.hardware || {};
const fill = (text, values) => Object.entries(values).reduce((t, [k, v]) => t.split(`{${k}}`).join(String(v)), text || "");

const TONES = {
  ok: { dot: "#16a34a", bg: "#ecfdf3", fg: "#14532d", border: "#bbf7d0" },
  checking: { dot: "#94a3b8", bg: "#f1f5f9", fg: "#334155", border: "#e2e8f0" },
  degraded: { dot: "#d97706", bg: "#fffbeb", fg: "#78350f", border: "#fde68a" },
  outdated: { dot: "#d97706", bg: "#fffbeb", fg: "#78350f", border: "#fde68a" },
  down: { dot: "#dc2626", bg: "#fef2f2", fg: "#7f1d1d", border: "#fecaca" },
  blocked: { dot: "#dc2626", bg: "#fef2f2", fg: "#7f1d1d", border: "#fecaca" },
};

export const chipLabel = (state) => {
  const chip = hw().chip || {};
  switch (state.status) {
    case "ok":
      return state.source === "printer" ? chip.ready_basic || "Hardware Controller running" : chip.ready || "Hardware ready";
    case "degraded":
      return state.detail || chip.printer_off || "Printer not connected";
    case "outdated":
      return chip.outdated || "Update Hardware Controller";
    case "down":
      return chip.down || "Hardware Controller off";
    case "blocked":
      return chip.blocked || "Browser blocking hardware";
    default:
      return chip.checking || "Checking hardware…";
  }
};

export const queueLabel = (count) => {
  const chip = hw().chip || {};
  return count === 1 ? chip.queue_one || "1 bill waiting to print" : fill(chip.queue_many || "{count} bills waiting to print", { count });
};

const timeOf = (ms) => {
  try {
    return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const btn = (primary) => ({
  border: `1px solid ${primary ? "#0F0718" : "#cbd5e1"}`,
  background: primary ? "#0F0718" : "#fff",
  color: primary ? "#fff" : "#0F0718",
  borderRadius: 6,
  padding: "2px 10px",
  fontSize: 12,
  cursor: "pointer",
  fontFamily: "Inter",
});

const Panel = ({ state }) => {
  const panel = hw().panel || {};
  const help = hw().help || {};
  const [busy, setBusy] = useState(false);
  const downloadUrl = getControllerDownloadUrl();
  const helpText = help[state.status === "outdated" ? "outdated" : state.status];
  const seconds = state.checkedAt ? Math.max(0, Math.round((Date.now() - state.checkedAt) / 1000)) : null;
  const run = async (fn) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="hc-panel" style={{ width: 300, fontFamily: "Inter", fontSize: 13, color: "#0F0718" }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{chipLabel(state)}</div>
      {helpText && <div style={{ color: "#475569", marginBottom: 8, lineHeight: 1.4 }}>{helpText}</div>}
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <button type="button" data-testid="hc-check-again" disabled={busy} style={btn(true)} onClick={() => run(() => checkHardwareNow({ manual: true }))}>
          {panel.check_again || "Check again"}
        </button>
        {seconds !== null && <span style={{ color: "#64748b", fontSize: 12 }}>{fill(panel.checked_ago || "Checked {seconds}s ago", { seconds })}</span>}
      </div>
      {(state.pole === "notSetUp" || state.pole === "neverWorked") && (
        <div data-testid="hc-pole-note" style={{ color: "#64748b", fontSize: 12, marginBottom: 8 }}>
          {state.pole === "notSetUp" ? panel.pole_not_set_up || "Customer display: not set up on this till" : panel.pole_not_responding || "Customer display: not responding"}
        </div>
      )}
      {downloadUrl && (state.status === "down" || state.status === "outdated") && (
        <div data-testid="hc-download" style={{ marginBottom: state.queue.length ? 12 : 0, paddingTop: 8, borderTop: "1px solid #e2e8f0" }}>
          <div style={{ color: "#475569", fontSize: 12, marginBottom: 6, lineHeight: 1.4 }}>
            {state.status === "outdated"
              ? panel.download_update || "Download the latest version, install it, then press Check again."
              : panel.download_note || "Not installed on this computer? Download it, run the installer, open it, then press Check again."}
          </div>
          <a
            data-testid="hc-download-link"
            href={downloadUrl}
            target="_blank"
            rel="noopener noreferrer"
            download
            style={{ ...btn(false), display: "inline-block", textDecoration: "none" }}
          >
            {panel.download || "Download Hardware Controller"}
          </a>
        </div>
      )}
      {state.queue.length > 0 && (
        <div data-testid="hc-queue">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <span style={{ fontWeight: 600 }}>
              {panel.queue_title || "Waiting to print"} ({state.queue.length})
            </span>
            <button type="button" data-testid="hc-print-all" disabled={busy} style={btn(false)} onClick={() => run(() => retryAllQueued())}>
              {panel.print_all || "Print all"}
            </button>
          </div>
          <div style={{ color: "#64748b", fontSize: 12, marginBottom: 6 }}>{panel.queue_note || "They will print automatically when the printer is ready."}</div>
          <div style={{ maxHeight: 200, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
            {state.queue.map((e) => {
              const label = messages?.print?.kinds?.[e.kind]?.label || e.kind;
              return (
                <div key={e.id} data-testid="hc-queue-item" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {label} {e.displayRef || e.ref} <span style={{ color: "#94a3b8" }}>{timeOf(e.createdAt)}</span>
                  </span>
                  <button type="button" disabled={busy} style={btn(false)} onClick={() => run(() => printQueuedNow(e.id))}>
                    {panel.print_now || "Print now"}
                  </button>
                  <button type="button" disabled={busy} style={{ ...btn(false), border: "none", color: "#64748b" }} onClick={() => removeQueuedPrint(e.id)}>
                    {panel.remove || "Remove"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const HardwareStatusChip = ({ style }) => {
  const state = useSyncExternalStore(subscribeHardwareStatus, getHardwareStatus);
  const enabled = isWatchdogEnabled();

  useEffect(() => {
    if (!enabled) return undefined;
    return startHardwareWatch();
  }, [enabled]);

  if (!enabled || state.status === "notConfigured") return null;
  const tone = TONES[state.status] || TONES.checking;
  const count = state.queue.length;

  return (
    <Popover trigger="click" placement="bottomRight" content={<Panel state={state} />}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginRight: "0.7rem", cursor: "pointer", minWidth: 0, ...style }}>
        <span
          data-testid="hc-chip"
          data-status={state.status}
          role="button"
          tabIndex={0}
          title={chipLabel(state)}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "2px 10px",
            borderRadius: 999,
            background: tone.bg,
            color: tone.fg,
            border: `1px solid ${tone.border}`,
            fontFamily: "Inter",
            fontSize: "0.75em",
            fontWeight: 500,
            whiteSpace: "nowrap",
            maxWidth: 220,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: tone.dot, flex: "none" }} />
          {chipLabel(state)}
        </span>
        {count > 0 && (
          <span
            data-testid="hc-queue-count"
            title={queueLabel(count)}
            style={{
              padding: "2px 8px",
              borderRadius: 999,
              background: "#fef3c7",
              color: "#78350f",
              border: "1px solid #fde68a",
              fontFamily: "Inter",
              fontSize: "0.75em",
              fontWeight: 600,
              whiteSpace: "nowrap",
            }}
          >
            {queueLabel(count)}
          </span>
        )}
      </div>
    </Popover>
  );
};

export default HardwareStatusChip;
